import { useEffect, useState } from 'react';

/**
 * The mobile playbook's breakpoints (docs/PLAYBOOK-mobile.md §2.2), as the
 * queries a component asks at runtime. The CSS uses the same three numbers;
 * a component reads them only where markup has to differ — a modal that is a
 * sheet, a bar that swaps its links for a menu button.
 */
export const PHONE = '(max-width: 600px)';
export const NARROW = '(max-width: 900px)';
/** The desktop layouts (docs/PLAYBOOK-mobile.md §2.2): where a side inspector replaces a modal (R6). */
export const DESKTOP = '(min-width: 1100px)';
/**
 * Where a popover becomes a bottom sheet (F5).
 *
 * M0 set this at 700px. M3 raised it to 900: at 768 all three submissions
 * popovers open off-screen — the 600px Filter panel at x = -272, the 420px
 * Columns panel at -7, the 400px Views panel 121px past the right edge — because
 * each is anchored to a toolbar button near the edge of the content column and
 * is wider than the room left beside it. Anchoring cannot be rescued at that
 * width without a magic offset, and §4F's tablet brief asks for sheets there
 * anyway. Above 900 the anchored panel is untouched.
 */
export const POPOVER_SHEET = '(max-width: 900px)';
export const COARSE = '(pointer: coarse)';
/**
 * Where the submission drawer's head folds into one disclosure (F2').
 *
 * Its own 700, not POPOVER_SHEET's: a tablet keeps the stacked head (§4F's
 * tablet brief) while its popovers became sheets, so the two numbers stopped
 * being the same one when M3 moved the popovers to 900.
 */
export const COMPACT_HEAD = '(max-width: 700px)';

/**
 * Does `query` match right now, re-rendering when that changes.
 *
 * jsdom has no `matchMedia`, so the absence of it means "no" rather than a
 * crash: every existing render test keeps its desktop layout, and a test that
 * wants the phone layout stubs `window.matchMedia` to say so.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => matchesNow(query));
  useEffect(() => {
    const mq = typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(query) : null;
    if (!mq) return;
    const on = () => setMatches(mq.matches);
    on();
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', on);
      return () => mq.removeEventListener('change', on);
    }
    // Safari before 14 only has the older pair.
    mq.addListener(on);
    return () => mq.removeListener(on);
  }, [query]);
  return matches;
}

/** One-off read of a media query; false wherever `matchMedia` is missing. */
export function matchesNow(query: string): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  try { return !!window.matchMedia(query)?.matches; } catch { return false; }
}
